import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor } from '@skylab-angular-shared/core';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '@skylab-shared/business';
import { HttpService, QueryHandler, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AddressDTO, AggregatedEmailStatusCode, EmailDeliveryStatus, InventoryDocumentKind, PurchaseDocumentKind } from '@skylab-shared/dataModel';
import { Mutable } from '@skylab-shared/types';
import { Transform, Type } from 'class-transformer';

interface RelatedDocument {
    documentId: number;
    kind: PurchaseDocumentKind | InventoryDocumentKind;
    number: string;
}

export type InventoryGoodsReceiptGridItem = InventoryGoodsReceiptGridItemDTO;

class InventoryGoodsReceiptGridItemDTO {
    public readonly id: number;
    public readonly version: string;
    public readonly kind: InventoryDocumentKind;
    public readonly documentNumber: string;
    public readonly externalId: string;

    public readonly sellerEmails: string[];

    public readonly seller: string;
    @Type(() => AddressDTO) public readonly sellerAddress?: AddressDTO;

    public readonly sellerId: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly receiptDate: Date;
    public readonly currencyId: number;
    public readonly favourite: boolean;
    public readonly issuer: string;
    public readonly note: string | null;

    public readonly totalNetValue: number;
    public readonly totalGrossValue: number;
    public readonly totalBaseCurrencyNetValue: number;
    public readonly totalBaseCurrencyGrossValue: number;

    public readonly relatedCommercialDocument: RelatedDocument;
    public readonly relatedInventoryDocument: RelatedDocument;

    public aggregatedEmailStatusCode: AggregatedEmailStatusCode;
    @Type(() => EmailDeliveryStatus) public emailStatuses: EmailDeliveryStatus[];
}

export class InventoryGoodsReceiptGridQuery extends PagedCollectionQuery<InventoryGoodsReceiptGridItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(InventoryGoodsReceiptGridQuery)
export class InventoryGoodsReceiptGridQueryHandler implements QueryHandler<InventoryGoodsReceiptGridQuery, PagedCollectionQueryResult<InventoryGoodsReceiptGridItem>> {
    #url = TaggedUrl.join('inventory/goodsReceipt', [Tags.Api]);
    #http = inject(Http) as HttpService;

    public async handleAsync(query: InventoryGoodsReceiptGridQuery): Promise<PagedCollectionQueryResult<InventoryGoodsReceiptGridItem>> {
        const { data } = await this.#http.get<Mutable<PagedCollectionQueryResult<InventoryGoodsReceiptGridItem>>>(this.#url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<InventoryGoodsReceiptGridItemDTO>(InventoryGoodsReceiptGridItemDTO)
        });

        return data;
    }
}
